import { TableCell, TableRow } from "@mui/material";
import { DotLegalEmptyState, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import NoSharings from "../../../processingActivity/processingActivityOverview/processingActivitySharingsTab/noSharings.svg?react";
import DotLegalPaper from "../../../common/components/dotLegalPaper/DotLegalPaper.tsx";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink.tsx";
import RiskIndicator from "../../../riskAssessment/riskIndicator/RiskIndicator.tsx";
import { RiskAssessmentType } from "../../../riskAssessment/RiskAssessment.types.ts";
import { RiskAssessmentArea, RiskComplianceArea } from "../../../riskAssessment/riskAssessments/RiskAssessments.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { useSystemProcessingActivitiesTab } from "./SystemProcessingActivitiesTab.hooks.ts";
import { SystemProcessingActivityViewModel } from "./SystemProcessingActivitiesTab.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";

export interface SystemProcessingActivitiesTabProps {
    systemId: string;
}

function SystemProcessingActivitiesTab(props: SystemProcessingActivitiesTabProps) {
    const { translateString } = useTranslation();
    const { getProcessingActivityOverviewUrl, getRiskAssessmentQuestionsDialog } = useUrlProvider();
    const { permissions } = useUserContext();
    const {
        isLoading,
        hasData,
        rows,
        currentRiskAssessmentVersionId,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
    } = useSystemProcessingActivitiesTab(props.systemId);

    function getTableHeaders() {
        const headers = Array<ITableHeader<SystemProcessingActivityViewModel>>();
        headers.push({ property: "name", text: translateString("processingActivity"), align: "left", showOnMobile: true });
        headers.push({ property: "owner", text: translateString("responsible"), align: "left", showOnMobile: true });
        headers.push({ property: "riskScore", text: translateString("risk"), align: "left", showOnMobile: true });
        headers.push({ property: "businessAreas", text: translateString("businessAreas"), align: "left", showOnMobile: true, notSortable: true });
        return headers;
    }

    return (
        <DotLegalPaper>
            {hasData ? (
                <DotLegalTableWithFilterBar
                    data={rows}
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noRelatedProcessingActivitiesFound")}
                    rowsPerPage={30}
                    headers={getTableHeaders()}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    clickableRows={false}
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    renderRow={(row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {permissions.processingActivityPermissions.read ? (
                                    <DotLegalLink bold linkColor={"primary"} to={getProcessingActivityOverviewUrl(row.id)}>
                                        {row.name}
                                    </DotLegalLink>
                                ) : (
                                    row.name
                                )}
                            </TableCell>
                            <TableCell>{row.owner}</TableCell>
                            <TableCell>
                                <RiskIndicator
                                    riskAssessmentType={RiskAssessmentType.None}
                                    riskScoreTextColor={"lightblue"}
                                    score={row.riskScore}
                                    hideRiskType
                                    removeLeftMargin
                                    linkHref={
                                        permissions.riskPermissions.read
                                            ? getRiskAssessmentQuestionsDialog(
                                                  row.id,
                                                  RiskAssessmentArea.ProcessingActivities,
                                                  RiskComplianceArea.GDPR,
                                                  currentRiskAssessmentVersionId ?? "null"
                                              )
                                            : undefined
                                    }
                                />
                            </TableCell>
                            <TableCell>
                                <ColoredChipsCell items={row.businessAreas} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<NoSharings />} text={translateString("noRelatedProcessingActivitiesFound")} />
            )}
        </DotLegalPaper>
    );
}

export default SystemProcessingActivitiesTab;
